/* eslint-disable no-unused-vars */
import IconButton from "@material-ui/core/IconButton";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import {
  Add,
  Assignment,
  EditTwoTone,
  FileCopy,
  PlaylistAddCheck,
} from "@material-ui/icons";
import PropTypes from "prop-types";
import * as React from "react";
import { useCallback, useMemo } from "react"; // Hooks importados
import CustomCell from "../../../Common/CustomCell";
import { getMinutesStatus } from "../../utils";

const CustomGroupRow = (props) => {
  const {
    columns,
    groups,
    options,
    level,
    groupData,
    actions,
    components,
    detailPanel,
    getFieldValue,
    icons,
    path,
    onGroupExpandChanged,
    onRowSelected,
    onRowClick,
    onToggleDetailPanel,
    onTreeExpandChanged,
    onEditingCanceled,
    onEditingApproved,
    hasAnyEditingRow,
    isTreeData,
    localization,
    handleGroupExpandChanged,
    // Props extras usadas no render
    t,
    canConfirmParticipants,
    onConfirmAttendance,
    canUpdateMinuteStatus,
    updateStatus,
    canExportMinuteFile,
    exportMinuteFIle,
    canCreateExtraSlot,
    createExtraSlot,
    canSendFileToSignature,
    sendFileToSignature,
    canSeeDocuments,
    handleViewDocuments,
  } = props;

  // useCallback para manter a referência da função de estilo estável
  const rotateIconStyle = useCallback((isOpen) => ({
    transform: isOpen ? "rotate(90deg)" : "none",
  }), []);

  // useCallback para o evento de clique, evitando recriação da função a cada render
  const handleExpandClick = useCallback((event) => {
    console.log("🚀 ~ CustomGroupRow ~ onGroupExpandChanged:",)
    if (onGroupExpandChanged) {
      onGroupExpandChanged(path);
    }
    if (handleGroupExpandChanged) {
      handleGroupExpandChanged();
    }
  }, [onGroupExpandChanged, path, handleGroupExpandChanged]);

  // useMemo para calcular o colSpan apenas quando as dependências mudarem
  const colSpan = useMemo(() => {
    let span = columns.filter((columnDef) => !columnDef.hidden).length;
    if (options.selection) span++;
    if (detailPanel) span++;
    if (actions && actions.length > 0) span++;
    return span;
  }, [columns, options.selection, detailPanel, actions]);

  // useMemo para evitar recalcular/recriar a lista de detalhes (linhas filhas) desnecessariamente
  const detail = useMemo(() => {
    if (!groupData.isExpanded) return null;

    if (groups.length > level + 1) {
      // Is there another group
      return groupData.groups.map((subGroupData, index) => (
        <components.GroupRow
          actions={actions}
          key={subGroupData.value || "" + index}
          columns={columns}
          components={components}
          detailPanel={detailPanel}
          getFieldValue={getFieldValue}
          groupData={subGroupData}
          groups={groups}
          icons={icons}
          level={level + 1}
          path={[...path, index]}
          onGroupExpandChanged={onGroupExpandChanged}
          onRowSelected={onRowSelected}
          onRowClick={onRowClick}
          onToggleDetailPanel={onToggleDetailPanel}
          onTreeExpandChanged={onTreeExpandChanged}
          onEditingCanceled={onEditingCanceled}
          onEditingApproved={onEditingApproved}
          options={options}
          hasAnyEditingRow={hasAnyEditingRow}
          isTreeData={isTreeData}
        />
      ));
    } else {
      return groupData.data.map((rowData, index) => {
        const key = index; // Usando index como key conforme original, idealmente seria um ID único
        if (rowData.tableData.editing) {
          return (
            <components.EditRow
              columns={columns}
              components={components}
              data={rowData}
              icons={icons}
              path={[...path, index]}
              localization={localization}
              key={key}
              mode={rowData.tableData.editing}
              options={options}
              isTreeData={isTreeData}
              detailPanel={detailPanel}
              onEditingCanceled={onEditingCanceled}
              onEditingApproved={onEditingApproved}
              getFieldValue={getFieldValue}
            />
          );
        } else {
          return (
            <components.Row
              actions={actions}
              key={key}
              columns={columns}
              components={components}
              data={rowData}
              detailPanel={detailPanel}
              getFieldValue={getFieldValue}
              icons={icons}
              path={[...path, index]}
              onRowSelected={onRowSelected}
              onRowClick={onRowClick}
              onToggleDetailPanel={onToggleDetailPanel}
              options={options}
              isTreeData={isTreeData}
              onTreeExpandChanged={onTreeExpandChanged}
              onEditingCanceled={onEditingCanceled}
              onEditingApproved={onEditingApproved}
              hasAnyEditingRow={hasAnyEditingRow}
            />
          );
        }
      });
    }
  }, [
    groupData,
    groups,
    level,
    actions,
    columns,
    components,
    detailPanel,
    getFieldValue,
    icons,
    path,
    onGroupExpandChanged,
    onRowSelected,
    onRowClick,
    onToggleDetailPanel,
    onTreeExpandChanged,
    onEditingCanceled,
    onEditingApproved,
    options,
    hasAnyEditingRow,
    isTreeData,
    localization
  ]);

  // useMemo para a criação das células de indentação
  const freeCells = useMemo(() => {
    const cells = [];
    for (let i = 0; i < level; i++) {
      cells.push(<TableCell padding="checkbox" key={i} />);
    }
    return cells;
  }, [level]);

  // Lógica de apresentação do valor da coluna
  const column = groups[level];
  let value = groupData.value;
  if (column.lookup) {
    value = column.lookup[value];
  }

  // Clonagem do título se necessário
  let title = column.title;
  if (typeof title !== "string") {
    title = React.cloneElement(title);
  }

  // Dados extraídos para limpeza do JSX
  const firstRowData = groupData.data[0];
  const meetingTitle = `Ata - ${firstRowData.committee} - ${firstRowData.meetDate}`;
  const isRoundRobin = firstRowData.roundRobin;
  const statusLabel = getMinutesStatus(firstRowData.minuteStatus, localStorage.getItem("i18nextLng"));

  return (
    <>
      <TableRow>
        {freeCells}
        <CustomCell
          colSpan={colSpan}
          padding="none"
          columnDef={column}
          value={value}
          icons={icons}
          groupData={groupData}
        >
          <div
            className="card d-flex flex-row align-items-center justify-content-between p-4"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "16px",
              paddingLeft: "12px",
              paddingRight: "6px",
            }}
          >
            {/* Coluna 1: Título e Round Robin */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingRight: "18px",
              }}
            >
              {meetingTitle}
              {isRoundRobin && (
                <div
                  style={{
                    marginLeft: "6px",
                    background: "#031795",
                    width: "20px",
                    padding: "12px",
                    color: "#fff",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    fontSize: "12px",
                  }}
                >
                  RR
                </div>
              )}
            </div>

            {/* Coluna 2: Status */}
            <div
              className="flex-column d-flex text-left"
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "16px",
              }}
            >
              <span className="text-muted mb-2">
                {statusLabel}
              </span>
            </div>

            {/* Coluna 3: Botões de Ação */}
            <div style={{ display: "flex" }}>
              <Tooltip aria-label="tooltip" title={t("forms.minutes.confirmAttendance")}>
                {/* Envolvendo em span caso precise de tooltip em botão desabilitado (fix material-ui) */}
                <span>
                    <IconButton disabled={!canConfirmParticipants} onClick={onConfirmAttendance}>
                    <PlaylistAddCheck />
                    </IconButton>
                </span>
              </Tooltip>
              <Tooltip aria-label="tooltip" title={t("forms.minutes.updateMinuteStatus")}>
                <span>
                    <IconButton disabled={!canUpdateMinuteStatus} onClick={updateStatus}>
                    <EditTwoTone />
                    </IconButton>
                </span>
              </Tooltip>
              <Tooltip aria-label="tooltip" title={t("forms.minutes.exportMinuteFile")}>
                <span>
                    <IconButton disabled={!canExportMinuteFile} onClick={exportMinuteFIle}>
                    <FileCopy />
                    </IconButton>
                </span>
              </Tooltip>
              <Tooltip aria-label="tooltip" title={t("forms.minutes.createExtraSlot")}>
                <span>
                    <IconButton disabled={!canCreateExtraSlot} onClick={createExtraSlot}>
                    <Add />
                    </IconButton>
                </span>
              </Tooltip>
              <Tooltip aria-label="tooltip" title={t("forms.minutes.signatures.sendFileToSign")}>
                <span>
                    <IconButton disabled={!canSendFileToSignature} onClick={sendFileToSignature}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-9gwpq2" focusable="false" aria-hidden="true" viewBox="0 0 24 24">
                        <path d="m18.85 10.39 1.06-1.06c.78-.78.78-2.05 0-2.83L18.5 5.09c-.78-.78-2.05-.78-2.83 0l-1.06 1.06zm-5.66-2.83L4 16.76V21h4.24l9.19-9.19zM19 17.5c0 2.19-2.54 3.5-5 3.5-.55 0-1-.45-1-1s.45-1 1-1c1.54 0 3-.73 3-1.5 0-.47-.48-.87-1.23-1.2l1.48-1.48c1.07.63 1.75 1.47 1.75 2.68M4.58 13.35C3.61 12.79 3 12.06 3 11c0-1.8 1.89-2.63 3.56-3.36C7.59 7.18 9 6.56 9 6c0-.41-.78-1-2-1-1.26 0-1.8.61-1.83.64-.35.41-.98.46-1.4.12-.41-.34-.49-.95-.15-1.38C3.73 4.24 4.76 3 7 3s4 1.32 4 3c0 1.87-1.93 2.72-3.64 3.47C6.42 9.88 5 10.5 5 11c0 .31.43.6 1.07.86z"></path>
                    </svg>
                    </IconButton>
                </span>
              </Tooltip>
              <Tooltip aria-label="tooltip" title={t("forms.minutes.signatures.viewDocuments")}>
                <span>
                    <IconButton disabled={!canSeeDocuments} onClick={handleViewDocuments}>
                    <Assignment />
                    </IconButton>
                </span>
              </Tooltip>
            </div>

            {/* Coluna 4: Contador e Expandir */}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <span className="d-flex align-items-center">
                {`${groupData.data.length} ${t("slots.title")} `}
                <IconButton
                  style={{
                    transition: "all ease 200ms",
                    ...rotateIconStyle(groupData.isExpanded),
                  }}
                  onClick={handleExpandClick}
                >
                  <icons.DetailPanel />
                </IconButton>
              </span>
            </div>
          </div>
        </CustomCell>
      </TableRow>
      {detail}
    </>
  );
};

CustomGroupRow.defaultProps = {
  columns: [],
  groups: [],
  options: {},
  level: 0,
};

CustomGroupRow.propTypes = {
  actions: PropTypes.array,
  columns: PropTypes.arrayOf(PropTypes.object),
  components: PropTypes.object,
  detailPanel: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.object),
  ]),
  getFieldValue: PropTypes.func,
  groupData: PropTypes.object,
  groups: PropTypes.arrayOf(PropTypes.object),
  hasAnyEditingRow: PropTypes.bool,
  icons: PropTypes.object,
  isTreeData: PropTypes.bool.isRequired,
  level: PropTypes.number,
  localization: PropTypes.object,
  onGroupExpandChanged: PropTypes.func,
  onRowSelected: PropTypes.func,
  onRowClick: PropTypes.func,
  onToggleDetailPanel: PropTypes.func.isRequired,
  onTreeExpandChanged: PropTypes.func.isRequired,
  onEditingCanceled: PropTypes.func,
  onEditingApproved: PropTypes.func,
  options: PropTypes.object,
  path: PropTypes.arrayOf(PropTypes.number),
  onConfirmAttendance: PropTypes.func,
  handleGroupExpandChanged: PropTypes.func,
  // Props implícitas adicionadas para validação
  t: PropTypes.func,
  canConfirmParticipants: PropTypes.bool,
  canUpdateMinuteStatus: PropTypes.bool,
  updateStatus: PropTypes.func,
  canExportMinuteFile: PropTypes.bool,
  exportMinuteFIle: PropTypes.func,
  canCreateExtraSlot: PropTypes.bool,
  createExtraSlot: PropTypes.func,
  canSendFileToSignature: PropTypes.bool,
  sendFileToSignature: PropTypes.func,
  canSeeDocuments: PropTypes.bool,
  handleViewDocuments: PropTypes.func,
};

// React.memo é recomendado aqui para evitar re-renderizações se as props não mudarem
export default React.memo(CustomGroupRow);